import React, { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { useProperty } from "@/context/PropertyContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, MapPin } from "lucide-react";
import { toast } from "sonner";
import PhotoUploader from "@/components/app/PhotoUploader";

export function resolveImageUrl(image_url) {
  if (!image_url) return null;
  if (image_url.startsWith("http://") || image_url.startsWith("https://")) return image_url;
  return `${API}/files?path=${encodeURIComponent(image_url)}`;
}

export default function Properties() {
  const { properties, refresh } = useProperty();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", location: "", type: "resort", image_url: "", active: true });

  const submit = async () => {
    try {
      if (editing) await api.put(`/properties/${editing.id}`, form);
      else await api.post(`/properties`, form);
      toast.success("Saved");
      setOpen(false); setEditing(null); setForm({ name: "", location: "", type: "resort", image_url: "", active: true });
      refresh();
    } catch (e) { toast.error("Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this property?")) return;
    await api.delete(`/properties/${id}`);
    toast.success("Deleted");
    refresh();
  };

  const startEdit = (p) => {
    setEditing(p);
    setForm({ name: p.name, location: p.location, type: p.type, image_url: p.image_url || "", active: p.active });
    setOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <span className="label-overline">Catalog</span>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-1">Properties</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-zinc-950 hover:bg-zinc-800" onClick={() => { setEditing(null); setForm({ name: "", location: "", type: "resort", image_url: "", active: true }); }} data-testid="add-property-button">
              <Plus className="w-4 h-4 mr-2" /> Add Property
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? "Edit Property" : "New Property"}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="property-name-input" />
              </div>
              <div>
                <Label>Location</Label>
                <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="property-location-input" />
              </div>
              <div>
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="property-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["resort", "villa", "hotel", "homestay", "boutique"].map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Property photo</Label>
                <div className="mt-2">
                  <PhotoUploader
                    value={form.image_url && !form.image_url.startsWith("http") ? form.image_url : null}
                    onChange={(p) => setForm({ ...form, image_url: p || "" })}
                    label="Upload property photo"
                    testid="property-photo"
                  />
                </div>
                <div className="mt-3">
                  <Label className="text-xs text-zinc-500">…or paste an external image URL</Label>
                  <Input
                    value={form.image_url && form.image_url.startsWith("http") ? form.image_url : ""}
                    onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                    placeholder="https://..."
                    data-testid="property-image-url"
                  />
                </div>
              </div>
            </div>
            <DialogFooter><Button onClick={submit} className="bg-zinc-950 hover:bg-zinc-800" data-testid="save-property-button">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {properties.map((p) => (
          <div key={p.id} className="bg-white border border-zinc-200 rounded-md overflow-hidden hover:border-zinc-300 transition-colors" data-testid={`property-card-${p.id}`}>
            {p.image_url ? (
              <img src={resolveImageUrl(p.image_url)} alt={p.name} className="w-full h-40 object-cover" />
            ) : (
              <div className="w-full h-40 bg-zinc-100" />
            )}
            <div className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <div className="label-overline">{p.type}</div>
                  <h3 className="font-display text-xl font-bold tracking-tight mt-1">{p.name}</h3>
                  <div className="text-sm text-zinc-500 mt-1 flex items-center gap-1.5"><MapPin className="w-3 h-3" /> {p.location}</div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => startEdit(p)} className="p-2 hover:bg-zinc-100 rounded" data-testid={`edit-property-${p.id}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => remove(p.id)} className="p-2 hover:bg-red-50 hover:text-red-600 rounded" data-testid={`delete-property-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="mt-3">
                <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded ${p.active ? "bg-emerald-50 text-emerald-700" : "bg-zinc-100 text-zinc-500"}`}>
                  {p.active ? "Active" : "Inactive"}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
